import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Isvg from 'react-inlinesvg';
import { t } from '@lingui/macro';
import { useDispatch, useSelector } from 'react-redux';
import { Cell, ConversationStatus, Result } from './styled';
import { getStatus, UNATTEMPTED } from './utils';
import {
  studentStatus as status,
  isDefined,
  getContextCoords
} from '../../helpers/utils';
import {
  getTopics,
  getActivityReportById
} from '../../containers/course-assign/course-assign-actions';
import TopicInfo from '../topic-info';
import ContextModal, { ContextModalWidth } from '../context-modal';
import OutsideClickHandler from '../../helpers/hoc/close/outside-click-handler';
import { ContextMenuAction } from '../activity-header/styled';
import Resources from '../../helpers/resources';
import { showToast } from '../toast/toast-action';
import {
  ERROR_DETECTED,
  UPDATE_STUDENT_ASSIGNMENT,
  SELECT_EQUAL_LESS_THAN_CELLS,
  UPDATE_STUDENT_ASSIGNMENTS_SELECTED
} from '../../containers/course-assign/course-assign-types';
import {
  retrieveAssignedTaskId,
  mapSelectedActivitiesToReq
} from '../../helpers/resources-helper';
import { Select, Results } from '../../helpers/icon-dictionary';
import { activateModal } from '../../containers/modal-manager/modal-manager-actions';
import { viewResultsModal } from '../results-modal/results.modal';
import {
  summarizeAssignments,
  useAssignmentFlowTracking
} from '../../analytics';

const StatusCell = (props) => {
  const {
    i18n,
    studentInfo,
    isCollapsed,
    hasResults,
    resultAverage,
    hasAssignments,
    activities,
    selectedCells,
    activityName,
    currentStudent,
    datePreference,
    courseId,
    classroomId,
    groupId,
    index
  } = props;

  const dispatch = useDispatch();
  const reassignFlow = useAssignmentFlowTracking('reassign');
  const [isModalOpen, setModalOpen] = useState(false);
  const [positionX, setPositionX] = useState(0);
  const [positionY, setPositionY] = useState(0);
  const [positionArrow, setPositionArrow] = useState(0);
  const [flowUp, setShouldFlowUp] = useState(false);

  let {
    assignment,
    result,
    isSelected,
    isFiltered,
    activityId,
    taskType,
    encryptedUserProfileId
  } = useSelector(
    (state) => state.CourseAssignReducer?.assignments.studentAssignments[index]
  );

  if (isCollapsed) {
    ({
      assignment,
      result,
      isSelected,
      activityId,
      isFiltered,
      taskType,
      encryptedUserProfileId
    } = studentInfo);
  }

  useEffect(() => {
    if (isModalOpen) {
      window.addEventListener('scroll', setPosition, true);
    }

    return () => window.removeEventListener('scroll', setPosition, true);
  });

  const shouldBeSelected = isCollapsed ? selectedCells > 0 : isSelected;

  const [isCellSelected, setIsCellSelected] = useState(shouldBeSelected);

  useEffect(() => {
    if (shouldBeSelected !== isCellSelected) {
      setIsCellSelected(shouldBeSelected);
    }
  }, [shouldBeSelected, isCellSelected]);

  const score = result && result.score;

  const cellRef = useRef(null);

  const assignStatus = isDefined(assignment?.assignStatus)
    ? assignment?.assignStatus
    : status[UNATTEMPTED];

  const isAssigned =
    assignStatus === status.ASSIGNED ||
    assignStatus === status.INPROGRESS ||
    assignStatus === status.PAST_DUE_DATE ||
    hasAssignments;

  const resultSummary = () => {
    if (resultAverage >= 0 && isCollapsed && hasResults) {
      return (
        <Result
          isFiltered={isFiltered}
          hasResults={hasResults}
          isCollapsed={isCollapsed}
          percentage={score}
          isAssigned={isAssigned}
        >{`${resultAverage.toFixed(1)}%`}</Result>
      );
    } else if (isDefined(score)) {
      return (
        <Result
          isFiltered={isFiltered}
          hasResults={hasResults}
          isCollapsed={isCollapsed}
          percentage={score}
          isAssigned={isAssigned}
        >{`${score}%`}</Result>
      );
    }
    return null;
  };

  const cellClickHandler = () => {
    if (isFiltered) {
      return;
    }
    if (isCollapsed) {
      setIsCellSelected(!isCellSelected);
      dispatch({
        type: UPDATE_STUDENT_ASSIGNMENTS_SELECTED,
        payload: {
          activities,
          encryptedUserProfileId
        }
      });
    } else if (isDefined(score)) {
      showContextModal();
    } else {
      selectCell();
    }
  };

  function selectCell() {
    setIsCellSelected(!isCellSelected);
    dispatch({
      type: UPDATE_STUDENT_ASSIGNMENT,
      index
    });
    setModalOpen(false);
  }

  // Select all less than or equal to cell result
  function selectAllBelow() {
    if (!isDefined(score)) {
      return;
    }
    dispatch({
      type: SELECT_EQUAL_LESS_THAN_CELLS,
      result: score,
      currentStudent
    });
    setModalOpen(false);
  }

  function setPosition() {
    const { posX, posY, posArrow, reverseArrow } = getContextCoords(
      cellRef.current.getBoundingClientRect(),
      ContextModalWidth
    );
    setPositionY(posY);
    setPositionX(posX);
    setPositionArrow(posArrow);
    setShouldFlowUp(reverseArrow);
  }

  function showContextModal() {
    setPosition();
    setModalOpen(!isModalOpen);
  }

  function getAssignmentDates() {
    if (assignment && assignment?.startDate) {
      return `${i18n.t('Generic.Assigned')`Assigned`} ${moment(
        assignment.startDate
      ).format('DD-MMM')} - ${
        assignment?.dueDate
          ? moment(assignment.dueDate).format('DD-MMM')
          : i18n.t('Generic.NoDueDate')`no due date`
      }`;
    }

    return moment(result?.completionDate).format('DD-MMM');
  }

  function handleSuccess(reassignment) {
    /* eslint-disable indent */
    dispatch(
      showToast(
        i18n._(
          reassignment
            ? t(
                'footer.reassignSuccess'
              )`Selected student activities have been reassigned`
            : t(
                'footer.unassignSuccess'
              )`Selected student activities have been unassigned`
        ),
        'success'
      )
    );
    /* eslint-enable indent */
    dispatch(getTopics(classroomId, groupId));
    setModalOpen(false);
  }

  function unassignCell() {
    Resources.unassign
      .put({ classroomId, groupId })
      .withBody({
        assignedTaskIdList: retrieveAssignedTaskId([studentInfo])
      })
      .send()
      .then(
        () => handleSuccess(false),
        (err) => {
          dispatch({
            type: ERROR_DETECTED,
            payload: err
          });
        }
      );
  }

  function reassign() {
    const startDatePreference = datePreference[0];
    const dueDatePreference = datePreference[1];
    /* eslint-disable indent */
    const startDate = isDefined(startDatePreference)
      ? moment(
          `${startDatePreference[0]} ${startDatePreference[1]}`
        ).toISOString()
      : undefined;
    const dueDate = isDefined(dueDatePreference)
      ? moment(`${dueDatePreference[0]} ${dueDatePreference[1]}`).toISOString()
      : undefined;
    /* eslint-enable indent */
    const requestBody = {
      courseId,
      startDate,
      dueDate,
      hasDueDate: dueDatePreference !== undefined,
      ...mapSelectedActivitiesToReq([studentInfo])
    };

    reassignFlow.start({
      target: 'student',
      dueDate: requestBody.dueDate,
      ...summarizeAssignments([studentInfo])
    });

    Resources.assign
      .post({ classroomId, groupId })
      .withBody(requestBody)
      .send()
      .then(
        () => {
          reassignFlow.confirm({
            target: 'student',
            ...summarizeAssignments([studentInfo])
          });
          handleSuccess(true);
        },
        (err) => {
          dispatch({
            type: ERROR_DETECTED,
            payload: err
          });
        }
      );
  }

  function RenderHandler(renderProps) {
    return isDefined(score) ? (
      <OutsideClickHandler
        callback={() => setModalOpen(false)}
        forButton={`StatusCell`}
      >
        {renderProps?.children}{' '}
      </OutsideClickHandler>
    ) : (
      <>{renderProps?.children}</>
    );
  }

  return (
    <RenderHandler>
      <Cell
        data-testid={`StatusCell_${encryptedUserProfileId}_${activityId}_${taskType}`}
        onClick={cellClickHandler}
        status={getStatus(assignStatus)}
        percentage={isCollapsed ? resultAverage : score}
        key={`statusCell_${activityId}_${taskType}`}
        isSelected={isCellSelected}
        isCollapsed={isCollapsed}
        isAssigned={isAssigned}
        hasResults={hasResults}
        isFiltered={isFiltered}
        isCellSelected={isCellSelected}
      >
        {
          <>
            <ConversationStatus ref={cellRef}>
              {resultSummary()}
            </ConversationStatus>
            {isModalOpen ? (
              <ContextModal
                show={isModalOpen}
                flowUp={flowUp}
                positionArrow={positionArrow}
                positionX={positionX}
                positionY={positionY}
              >
                <TopicInfo
                  displayHideOption={false}
                  standardName={`${currentStudent.firstName} ${currentStudent.lastName}`}
                  subName={activityName}
                  externalKey={getAssignmentDates()}
                  ljName={`${result?.attempts} ${
                    result?.attempts > 1
                      ? i18n.t('StatusCell.Attempts')`attempts`
                      : i18n.t('StatusCell.Attempt')`attempt`
                  }`}
                />
                <ContextMenuAction
                  data-testid="select-cell"
                  role="button"
                  first
                  tabIndex="-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    selectCell();
                  }}
                >
                  <Isvg src={Select} cacheGetRequests />
                  {i18n.t('StatusCell.SelectCell')`Select cell`}
                </ContextMenuAction>
                <ContextMenuAction
                  data-testid="select-all-less-than"
                  role="button"
                  first
                  tabIndex="-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    selectAllBelow();
                  }}
                >
                  {
                    /* eslint-disable indent */
                    `${i18n.t(
                      'StatusCell.SelectLessThan'
                    )`Select all <=`} ${score}%`
                    /* eslint-enable indent */
                  }
                </ContextMenuAction>
                <ContextMenuAction
                  data-testid="unassign-from-student"
                  role="button"
                  first
                  tabIndex="-1"
                  blackListed={!isDefined(studentInfo.assignment)}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isDefined(studentInfo.assignment)) {
                      unassignCell();
                    }
                  }}
                >
                  {`${i18n.t('StatusCell.Unassign')`Unassign from student`}`}
                </ContextMenuAction>
                <ContextMenuAction
                  data-testid="reassign"
                  role="button"
                  first
                  tabIndex="-1"
                  blackListed={isDefined(studentInfo.assignment)}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isDefined(studentInfo.assignment)) {
                      reassign();
                    }
                  }}
                >
                  {`${i18n.t('StatusCell.Reassign')`Reassign`}`}
                </ContextMenuAction>
                <ContextMenuAction
                  data-testid="ViewResults"
                  role="button"
                  last
                  tabIndex="-1"
                  onClick={() => {
                    dispatch(
                      getActivityReportById(
                        classroomId,
                        encryptedUserProfileId,
                        activityId,
                        taskType
                      )
                    );
                    dispatch(activateModal(viewResultsModal(i18n, isAssigned)));
                  }}
                >
                  <Isvg src={Results} cacheGetRequests />
                  {`${i18n.t('StatusCell.viewResults')`View Results`}`}
                </ContextMenuAction>
              </ContextModal>
            ) : null}
          </>
        }
      </Cell>
    </RenderHandler>
  );
};

StatusCell.propTypes = {
  studentInfo: PropTypes.object,
  activityName: PropTypes.string,
  isCollapsed: PropTypes.bool,
  hasResults: PropTypes.bool,
  hasAssignments: PropTypes.bool,
  resultAverage: PropTypes.number,
  activities: PropTypes.array,
  selectedCells: PropTypes.number,
  i18n: PropTypes.object,
  currentStudent: PropTypes.object.isRequired,
  datePreference: PropTypes.array.isRequired,
  courseId: PropTypes.number.isRequired,
  classroomId: PropTypes.number.isRequired,
  index: PropTypes.number,
  groupId: PropTypes.number.isRequired
};

StatusCell.defaultProps = {
  isCollapsed: false,
  hasAssignments: false,
  activityIds: [],
  selectedCells: 0,
  index: 0
};

export default StatusCell;



// WEBPACK FOOTER //
// ./components/status-cell/index.js